/**
 * ContactListDialog
 * Reusable dialog for displaying a drilldown list of contacts with optional status badge.
 * Used in EscalationsTab for flag/SLA drilldowns.
 */

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Phone, User, ExternalLink } from "lucide-react"
import type { FlagBreakdownContact } from "../../hooks/useAnalytics"

interface ContactListDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  contacts: FlagBreakdownContact[]
  showStatusBadge?: boolean
  emptyMessage?: string
}

export function ContactListDialog({
  open,
  onOpenChange,
  title,
  contacts,
  showStatusBadge = false,
  emptyMessage = "No contacts",
}: ContactListDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            {title}
            <Badge variant="outline" className="text-[10px] font-normal">
              {contacts.length} contacts
            </Badge>
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[400px]">
          <div className="space-y-1">
            {contacts.map((contact, i) => (
              <div
                key={`${contact.contactId}-${i}`}
                className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-muted/50 group"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {showStatusBadge && (
                    <Badge
                      variant={contact.status === "active" ? "destructive" : "secondary"}
                      className="text-[9px] px-1.5 py-0 shrink-0"
                    >
                      {contact.status}
                    </Badge>
                  )}
                  <div className="min-w-0">
                    <div className="text-xs font-medium flex items-center gap-1">
                      <Phone className="h-3 w-3 shrink-0 text-muted-foreground" />
                      <span className="truncate">{contact.phoneNumber || contact.contactId}</span>
                    </div>
                    {contact.name && (
                      <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <User className="h-2.5 w-2.5 shrink-0" />
                        <span className="truncate">{contact.name}</span>
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                  onClick={() => window.open(`/moderation?contact=${contact.contactId}`, "_blank")}
                >
                  <ExternalLink className="h-3 w-3 mr-1" />Moderation
                </Button>
              </div>
            ))}
            {contacts.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4">{emptyMessage}</p>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
